import { useState, useEffect, useCallback, useRef } from 'react';
import { getCurrentCyclePeriod } from '../core/dateHelpers.js';

export function useData() {
    // ── Core Data State ─────────────────────────────────────
    const [goldInventory, setGoldInventory] = useState([]);
    const [personalLoans, setPersonalLoans] = useState([]);
    const [goldLoans, setGoldLoans] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [creditCards, setCreditCards] = useState([]);
    const [sgBudgetCycle1, setSgBudgetCycle1] = useState([]);
    const [sgBudgetCycle2, setSgBudgetCycle2] = useState([]);
    const [lkBudgetDamith, setLkBudgetDamith] = useState([]);
    const [lkBudgetShan, setLkBudgetShan] = useState([]);
    const [sgdRate, setSgdRate] = useState(186);
    const [lkrRate, setLkrRate] = useState(245);
    const [usdRate, setUsdRate] = useState(1.26);
    const [baseCurrency, setBaseCurrency] = useState('SGD');

    // ── Investments State ────────────────────────────────────
    const DEFAULT_INVESTMENTS = [
        { id: 1, ticker: 'NVDA', name: 'NVIDIA Corp', units: 0.25, avgCostUsd: 130.00, currentPriceUsd: 130.00, currency: 'USD', type: 'stock' },
        { id: 2, ticker: 'VOO', name: 'Vanguard S&P 500 ETF', units: 9, avgCostUsd: 540.00, currentPriceUsd: 540.00, currency: 'USD', type: 'etf' },
    ];
    const DEFAULT_MMF = [
        { id: 1, name: 'Fullerton SGD Cash Fund', currency: 'SGD', units: 0, nav: 1.00, totalValue: 0 },
        { id: 2, name: 'CSOP USD Money Market Fund', currency: 'USD', units: 0, nav: 1.00, totalValue: 0 },
    ];
    const [investments, setInvestments] = useState([]);
    const [invCashSGD, setInvCashSGD] = useState(0);
    const [invCashUSD, setInvCashUSD] = useState(0);
    const [moneyMarketFunds, setMoneyMarketFunds] = useState([]);

    // ── Income Tracking State ──────────────────────────────
    const [monthlyIncomes, setMonthlyIncomes] = useState([]);

    // ── Financial Milestone Journal ────────────────────────
    const [financialMilestones, setFinancialMilestones] = useState([]);

    // ── Fixed Assets State ─────────────────────────────────
    const DEFAULT_FIXED_ASSETS = [
        {
            id: 1, name: 'Land — Kadawatha (20 perch)', type: 'land',
            location: '1st Lane, Dawatagahawatta, Suriyapaluwa, Kadawatha, Sri Lanka',
            area: '20 perch',
            cmvLKR: 22000000,
            fsvLKR: 19000000,
            valuationLKR: 22000000,
            lastValuationDate: 'May 2026',
            builtYear: '',
            notes: 'Residential interior corridor. CMV LKR 1.1M/perch, FSV LKR 950K/perch.',
        },
        {
            id: 2, name: 'House 1 — Kadawatha (Secondary Residence)', type: 'house',
            location: '1st Lane, Dawatagahawatta, Suriyapaluwa, Kadawatha, Sri Lanka',
            area: '1,100 sq ft',
            cmvLKR: 3500000,
            fsvLKR: 2500000,
            valuationLKR: 3500000,
            lastValuationDate: 'May 2026',
            builtYear: '2000',
            notes: '3BR/2BA single-story. Residual shell value.',
        },
        {
            id: 3, name: 'House 2 — Kadawatha (Luxury Executive Residence)', type: 'house',
            location: '1st Lane, Dawatagahawatta, Suriyapaluwa, Kadawatha, Sri Lanka',
            area: 'Multi-level, custom architect design',
            cmvLKR: 34000000,
            fsvLKR: 27500000,
            valuationLKR: 34000000,
            lastValuationDate: 'May 2026',
            builtYear: '2025',
            notes: 'Premium teak/nadun/jak timber frames, polished titanium surfaces.',
        },
    ];
    const [fixedAssets, setFixedAssets] = useState([]);

    // ── Budget Tracker State ───────────────────────────────
    const [cycleHistory, setCycleHistory] = useState([]);
    const [cycleOpenedAt, setCycleOpenedAt] = useState(null);
    const [monthlySnapshots, setMonthlySnapshots] = useState([]);

    // ── Apply-origin flag: true while applyData() is running ──
    // Used by App.jsx to skip marking dirty when Drive data is applied.
    const isApplyingRef = useRef(false);

    // ── ID Migration Helpers ───────────────────────────────
    const slugifyKey = useCallback((s) => String(s || '').toLowerCase()
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60), []);

    const migrateIds = useCallback((arr, prefix) => {
        if (!Array.isArray(arr)) return arr;
        const seen = new Set();
        return arr.map((item, idx) => {
            if (item && item.id != null && item.id !== '') {
                seen.add(item.id);
                return item;
            }
            const parts = [
                item.name || item.accountName || item.cardName || item.ticker || '',
                item.owner || '',
                item.type || '',
                item.quality || '',
                item.weight != null ? String(item.weight) : '',
                item.bank || '',
            ].filter(Boolean);
            let key = `${prefix}-${slugifyKey(parts.join('-')) || `idx${idx}`}`;
            let attempt = key, n = 2;
            while (seen.has(attempt)) {
                attempt = `${key}-${n++}`;
            }
            seen.add(attempt);
            return { ...item, id: attempt };
        });
    }, [slugifyKey]);

    // ── Get All Data (for sync) ────────────────────────────
    const getAllData = useCallback(() => ({
        goldInventory, personalLoans, goldLoans, accounts, creditCards,
        sgBudgetCycle1, sgBudgetCycle2, lkBudgetDamith, lkBudgetShan,
        sgdRate: typeof sgdRate === 'number' ? sgdRate : (parseFloat(sgdRate) || 186),
        lkrRate: typeof lkrRate === 'number' ? lkrRate : (parseFloat(lkrRate) || 245),
        usdRate: typeof usdRate === 'number' ? usdRate : (parseFloat(usdRate) || 1.26),
        baseCurrency: baseCurrency || 'SGD',
        cycleHistory,
        cycleOpenedAt,
        monthlySnapshots,
        investments,
        invCashSGD,
        invCashUSD,
        moneyMarketFunds,
        fixedAssets,
        monthlyIncomes,
        financialMilestones,
        version: '3.2',
    }), [goldInventory, personalLoans, goldLoans, accounts, creditCards,
        sgBudgetCycle1, sgBudgetCycle2, lkBudgetDamith, lkBudgetShan,
        sgdRate, lkrRate, usdRate, baseCurrency, cycleHistory, cycleOpenedAt, monthlySnapshots,
        investments, invCashSGD, invCashUSD, moneyMarketFunds, fixedAssets, monthlyIncomes, financialMilestones]);

    // ── Apply Data (from sync or load) ─────────────────────
    const applyData = useCallback((data) => {
        isApplyingRef.current = true;
        const migrateBudgetItems = (items) =>
            (items || []).map(i => ({
                ...i,
                actualSpent: (i.actualSpent !== undefined && i.actualSpent !== null) ? i.actualSpent : 0,
                spendLog: Array.isArray(i.spendLog) ? i.spendLog : [],
            }));
        const migrateLoans = (loans) =>
            (loans || []).map(l => ({
                ...l,
                paymentHistory: Array.isArray(l.paymentHistory) ? l.paymentHistory : [],
            }));
        const migrateFA = (stored) => {
            if (!Array.isArray(stored) || stored.length === 0) return DEFAULT_FIXED_ASSETS;
            const allZero = stored.every(fa => !(fa.cmvLKR > 0) && !(fa.fsvLKR > 0) && !(fa.valuationLKR > 0));
            if (allZero) return DEFAULT_FIXED_ASSETS;
            return stored.map(fa => ({
                ...fa,
                cmvLKR: fa.cmvLKR != null ? fa.cmvLKR : (fa.valuationLKR || 0),
                fsvLKR: fa.fsvLKR != null ? fa.fsvLKR : 0,
                valuationLKR: fa.cmvLKR != null ? fa.cmvLKR : (fa.valuationLKR || 0),
                builtYear: fa.builtYear || '',
                notes: fa.notes || '',
            }));
        };

        setGoldInventory(migrateIds(data.goldInventory || [], 'gold'));
        setPersonalLoans(migrateIds(migrateLoans(data.personalLoans), 'ploan'));
        setGoldLoans(migrateIds(migrateLoans(data.goldLoans), 'gloan'));
        setAccounts(migrateIds(data.accounts || [], 'acct'));
        setCreditCards(migrateIds(data.creditCards || [], 'cc'));
        setSgBudgetCycle1(migrateBudgetItems(data.sgBudgetCycle1));
        setSgBudgetCycle2(migrateBudgetItems(data.sgBudgetCycle2));
        setLkBudgetDamith(migrateBudgetItems(data.lkBudgetDamith));
        setLkBudgetShan(migrateBudgetItems(data.lkBudgetShan));
        setSgdRate(data.sgdRate || 186);
        setLkrRate(data.lkrRate || 245);
        setUsdRate(data.usdRate || 1.26);
        setBaseCurrency(data.baseCurrency || 'SGD');
        setCycleHistory(Array.isArray(data.cycleHistory)
            ? [...new Map(data.cycleHistory.map(c => [c.cycleId || c.period, c])).values()]
            : []);
        setCycleOpenedAt(data.cycleOpenedAt || getCurrentCyclePeriod().cycleId);
        setMonthlySnapshots(Array.isArray(data.monthlySnapshots) ? data.monthlySnapshots : []);
        setInvestments(migrateIds(Array.isArray(data.investments) ? data.investments : DEFAULT_INVESTMENTS, 'inv'));
        setInvCashSGD(data.invCashSGD || 0);
        setInvCashUSD(data.invCashUSD || 0);
        setMoneyMarketFunds(migrateIds(Array.isArray(data.moneyMarketFunds) ? data.moneyMarketFunds : DEFAULT_MMF, 'mmf'));
        setFixedAssets(migrateIds(migrateFA(data.fixedAssets), 'fa'));
        setMonthlyIncomes(migrateIds(Array.isArray(data.monthlyIncomes) ? data.monthlyIncomes : [], 'inc'));
        setFinancialMilestones(migrateIds(Array.isArray(data.financialMilestones) ? data.financialMilestones : [], 'ms'));
        // Reset flag after React has flushed the batch of state updates
        setTimeout(() => { isApplyingRef.current = false; }, 0);
    }, [migrateIds]);

    // ── Initial Data Load ──────────────────────────────────
    useEffect(() => {
        const loadData = (data) => {
            const migrateBudgetItems = (items) =>
                (items || []).map(i => ({
                    ...i,
                    actualSpent: (i.actualSpent !== undefined && i.actualSpent !== null) ? i.actualSpent : 0,
                    spendLog: Array.isArray(i.spendLog) ? i.spendLog : [],
                }));
            const migrateLoans = (loans) =>
                (loans || []).map(l => ({
                    ...l,
                    paymentHistory: Array.isArray(l.paymentHistory) ? l.paymentHistory : [],
                }));
            const migrateFixedAssets = (stored) => {
                if (!Array.isArray(stored) || stored.length === 0) return DEFAULT_FIXED_ASSETS;
                const allZero = stored.every(fa => !(fa.cmvLKR > 0) && !(fa.fsvLKR > 0) && !(fa.valuationLKR > 0));
                if (allZero) return DEFAULT_FIXED_ASSETS;
                return stored.map(fa => ({
                    ...fa,
                    cmvLKR: fa.cmvLKR != null ? fa.cmvLKR : (fa.valuationLKR || 0),
                    fsvLKR: fa.fsvLKR != null ? fa.fsvLKR : 0,
                    valuationLKR: fa.cmvLKR != null ? fa.cmvLKR : (fa.valuationLKR || 0),
                    builtYear: fa.builtYear || '',
                    notes: fa.notes || '',
                }));
            };

            setGoldInventory(migrateIds(data.goldInventory || [], 'gold'));
            setPersonalLoans(migrateIds(migrateLoans(data.personalLoans), 'ploan'));
            setGoldLoans(migrateIds(migrateLoans(data.goldLoans), 'gloan'));
            setAccounts(migrateIds(data.accounts || [], 'acct'));
            setCreditCards(migrateIds(data.creditCards || [], 'cc'));
            setSgBudgetCycle1(migrateBudgetItems(data.sgBudgetCycle1));
            setSgBudgetCycle2(migrateBudgetItems(data.sgBudgetCycle2));
            setLkBudgetDamith(migrateBudgetItems(data.lkBudgetDamith));
            setLkBudgetShan(migrateBudgetItems(data.lkBudgetShan));
            setSgdRate(data.sgdRate || 186);
            setLkrRate(data.lkrRate || 245);
            setUsdRate(data.usdRate || 1.26);
            setBaseCurrency(data.baseCurrency || 'SGD');
            setCycleHistory(Array.isArray(data.cycleHistory)
                ? [...new Map(data.cycleHistory.map(c => [c.cycleId || c.period, c])).values()]
                : []);
            setCycleOpenedAt(data.cycleOpenedAt || getCurrentCyclePeriod().cycleId);
            setMonthlySnapshots(Array.isArray(data.monthlySnapshots) ? data.monthlySnapshots : []);
            setInvestments(migrateIds(Array.isArray(data.investments) ? data.investments : DEFAULT_INVESTMENTS, 'inv'));
            setInvCashSGD(data.invCashSGD || 0);
            setInvCashUSD(data.invCashUSD || 0);
            setMoneyMarketFunds(migrateIds(Array.isArray(data.moneyMarketFunds) ? data.moneyMarketFunds : DEFAULT_MMF, 'mmf'));
            setFixedAssets(migrateIds(migrateFixedAssets(data.fixedAssets), 'fa'));
            setMonthlyIncomes(migrateIds(Array.isArray(data.monthlyIncomes) ? data.monthlyIncomes : [], 'inc'));
            setFinancialMilestones(migrateIds(Array.isArray(data.financialMilestones) ? data.financialMilestones : [], 'ms'));
        };

        const savedData = localStorage.getItem('iPlannerData_v3');
        if (savedData) {
            loadData(JSON.parse(savedData));
        } else {
            fetch('./data.json')
                .then(res => { if (!res.ok) throw new Error('No data.json'); return res.json(); })
                .then(data => { console.log('[iPlanner] Imported from data.json'); loadData(data); })
                .catch(() => console.log('[iPlanner] Starting fresh'));
        }
    }, [migrateIds]);

    return {
        // State
        goldInventory, setGoldInventory,
        personalLoans, setPersonalLoans,
        goldLoans, setGoldLoans,
        accounts, setAccounts,
        creditCards, setCreditCards,
        sgBudgetCycle1, setSgBudgetCycle1,
        sgBudgetCycle2, setSgBudgetCycle2,
        lkBudgetDamith, setLkBudgetDamith,
        lkBudgetShan, setLkBudgetShan,
        sgdRate, setSgdRate,
        lkrRate, setLkrRate,
        usdRate, setUsdRate,
        baseCurrency, setBaseCurrency,
        investments, setInvestments,
        invCashSGD, setInvCashSGD,
        invCashUSD, setInvCashUSD,
        moneyMarketFunds, setMoneyMarketFunds,
        fixedAssets, setFixedAssets,
        monthlyIncomes, setMonthlyIncomes,
        financialMilestones, setFinancialMilestones,
        cycleHistory, setCycleHistory,
        cycleOpenedAt, setCycleOpenedAt,
        monthlySnapshots, setMonthlySnapshots,
        // Helpers
        DEFAULT_INVESTMENTS, DEFAULT_MMF, DEFAULT_FIXED_ASSETS,
        migrateIds,
        getAllData,
        applyData,
        isApplyingRef,
    };
}
